import { useSourcePluginContext } from '../../context/SourcePluginContext'
import { memo, useCallback, useState } from 'react'
import useLabAppointments from '../../hooks/useLabAppointments'
import { Table } from 'react-bootstrap'
import Loader from '../../components/Loader'
import LabAppointmentRow from './LabAppointmentRow'
import SortableHeader from './SortableHeader'

const LabAppointments: React.FC = () => {
  const { pluginContext } = useSourcePluginContext()
  const { labAppointments, isLoading, error, isSuccess } = useLabAppointments(
    pluginContext?.member ?? undefined
  )

  const [sortField, setSortField] = useState<'status' | 'scheduledAt'>('status')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  // Sort lab appointments by selected field
  const sortedLabAppointments = [...labAppointments].sort((a, b) => {
    let comparison = 0

    if (sortField === 'status') {
      comparison = (a.status || '').localeCompare(b.status || '')
    } else if (sortField === 'scheduledAt') {
      const dateA = new Date(a.scheduledAt).getTime()
      const dateB = new Date(b.scheduledAt).getTime()
      comparison = dateA - dateB
    }

    return sortOrder === 'asc' ? comparison : -comparison
  })

  const handleSortToggle = useCallback(
    (field: 'status' | 'scheduledAt') => {
      if (sortField === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortField(field)
        setSortOrder('asc')
      }
    },
    [sortField]
  )

  if (error) {
    return <div>Error loading lab appointments</div>
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="mt-6 px-6">
      <p className="h1 mb-4 mt-2 fs-12 text-center">Lab Appointments</p>

      <br />
      {isSuccess && (
        <div className="lab-appointments-table-container">
          <Table striped bordered hover>
            <thead>
              <tr className="text-center">
                <th>Lab Provider</th>
                <th>Lab Location</th>
                <th>Confirmation Number</th>
                <th>
                  <SortableHeader
                    title="Appointment Date/Time"
                    isActive={sortField === 'scheduledAt'}
                    sortOrder={sortOrder}
                    onClick={() => handleSortToggle('scheduledAt')}
                  />
                </th>
                <th>
                  <SortableHeader
                    title="Status"
                    isActive={sortField === 'status'}
                    sortOrder={sortOrder}
                    onClick={() => handleSortToggle('status')}
                  />
                </th>
                <th>Cancellation Reason</th>
              </tr>
            </thead>
            <tbody>
              {sortedLabAppointments.map((labAppointment, index) => (
                <LabAppointmentRow
                  key={index}
                  labAppointment={labAppointment}
                  index={index}
                />
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default memo(LabAppointments)
